import React, { useMemo, useState } from "react";
import { Chip, IconButton, Tooltip } from "@mui/material";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import MoreTimeIcon from "@mui/icons-material/MoreTime";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DangerousIcon from "@mui/icons-material/Dangerous";
import { ThemeProvider } from "@mui/system";

import * as DefaultRoutes from "@Routes/DefaultRoutes";
import { GET_MY_EXTENSION_REQUESTS, RESPOND_TO_EXTENSION_REQUEST } from "@API/APIRoutes";
import usePaginate from "@Hooks/usePaginate";
import usePostItem from "@Hooks/usePostItem";
import GenericExitRequestListing from "@Pages/GenericExitRequestListing/GenericExitRequestListing";
import MeatballsMenu from "@Components/MeatballsMenu/MeatballsMenu";
import DelegationReasonPopup from "@Components/DelegationReasonPopup/DelegationReasonPopup";
import DelegationActionPopup from "@Components/DelegationActionPopup/DelegationActionPopup";
import { stringCustomOperators } from "@Utils/Validations/HelperFunctions/CustomFilters";

const statusColor = {
  PENDING: "warning",
  APPROVED: "success",
  REJECTED: "error",
};

const phaseLabel = {
  IA: "Impact Assessment",
  EXIT_PLAN: "Exit Plan",
};

const roleTitle = {
  REGULATORY: "Extension Requests",
  "R&D": "My Extension Requests",
  RD_MANAGER: "My Extension Requests",
};

const ExtensionRequestsListing = () => {
  const navigate = useNavigate();
  const currentRole = useSelector((state) => state.user.role);
  const isRegulatory = currentRole === "REGULATORY";
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showReasonPopup, setShowReasonPopup] = useState(false);
  const [showActionPopup, setShowActionPopup] = useState(false);
  const [actionType, setActionType] = useState("");
  const { loading: pageLoading, getPage } = usePaginate();
  const { loading: actionLoading, postItem } = usePostItem();
  const errorMessage = "Failed to fetch extension requests.";

  const fetchPage = (queryOptions, filterModel) => {
    const filterItems = (filterModel?.items || [])
      .map((item) => {
        const cleaned = { ...item };
        if (cleaned.value && typeof cleaned.value === "string") {
          cleaned.value = cleaned.value.trim();
        }
        return cleaned;
      })
      .filter((item) => item.field && item.operator);

    getPage(GET_MY_EXTENSION_REQUESTS, errorMessage, queryOptions, filterItems).then((data) => {
      if (!data) return;
      setRows(data.content || []);
      setRowCount(data.totalElements || 0);
    });
  };

  const getStatusLabelColor = (status) => statusColor[status] || "default";

  const formatDate = (date) => (date ? dayjs(date).format("MM/DD/YYYY") : "-");

  const openReasonPopup = (request) => {
    setSelectedRequest(request);
    setShowReasonPopup(true);
  };

  const closeReasonPopup = () => {
    setSelectedRequest(null);
    setShowReasonPopup(false);
  };

  const openDetailsPage = (request) => {
    navigate(
      DefaultRoutes.extensionRequestDetail.replace(
        ":extensionRequestId",
        request.id
      ),
      { state: { requestData: request } }
    );
  };

  const openActionPopup = (request, nextAction) => {
    setSelectedRequest(request);
    setActionType(nextAction);
    setShowActionPopup(true);
  };

  const closeActionPopup = () => {
    setSelectedRequest(null);
    setActionType("");
    setShowActionPopup(false);
  };

  const handleRespond = async (responseMessage = null) => {
    if (!selectedRequest) return;

    const result = await postItem(
      RESPOND_TO_EXTENSION_REQUEST,
      {
        extensionRequestId: selectedRequest.id,
        approved: actionType === "approve",
        responseMessage,
      },
      () => {},
      actionType === "approve"
        ? "Extension request approved!"
        : "Extension request rejected!",
      "Failed to respond to extension request.",
      true
    );

    if (!result) {
      return;
    }

    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id === selectedRequest.id
          ? {
              ...row,
              status: result.status,
              responseMessage: result.responseMessage,
              respondedAt: result.respondedAt,
              effectiveEscalationDate: result.effectiveEscalationDate,
            }
          : row
      )
    );
    closeActionPopup();
  };

  const meatballsMenuOptions = (request) => [
    {
      label: "Approve Extension Request",
      icon: <CheckCircleIcon />,
      color: "green",
      action: () => openActionPopup(request, "approve"),
    },
    {
      label: "Reject Extension Request",
      icon: <DangerousIcon />,
      color: "red",
      action: () => openActionPopup(request, "reject"),
    },
  ];

  const columns = useMemo(
    () => [
      {
        field: "ingredient",
        headerName: "Ingredient",
        flex: 1.3,
        type: "string",
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <div>
            <div>{params.row.exitRequestIngredientName}</div>
            <div style={{ fontSize: "0.75rem", color: "gray" }}>
              CAS: {params.row.exitRequestIngredientCasNumber}
            </div>
          </div>
        ),
        filterOperators: stringCustomOperators,
      },
      {
        field: "categoryName",
        headerName: "Category",
        flex: 1.2,
        type: "string",
        sortable: false,
        filterOperators: stringCustomOperators,
      },
      {
        field: "requesterUsername",
        headerName: "Requested By",
        flex: 1.2,
        type: "string",
        sortable: false,
        filterOperators: stringCustomOperators,
      },
      {
        field: "phase",
        headerName: "Phase",
        flex: 0.9,
        type: "string",
        sortable: false,
        renderCell: (params) => (
          <Chip
            label={phaseLabel[params.row.phase] || params.row.phase}
            size="small"
            variant="outlined"
          />
        ),
      },
      {
        field: "requestedWeeks",
        headerName: "Weeks",
        flex: 0.8,
        type: "number",
        sortable: false,
      },
      {
        field: "baseEscalationDate",
        headerName: "Current Deadline",
        flex: 1,
        type: "date",
        sortable: false,
        filterable: false,
        valueGetter: (params) =>
          params.row.baseEscalationDate ? new Date(params.row.baseEscalationDate) : null,
        valueFormatter: (params) => formatDate(params.value),
      },
      {
        field: "extendedEscalationDate",
        headerName: "Requested Deadline",
        flex: 1,
        type: "date",
        sortable: false,
        filterable: false,
        valueGetter: (params) =>
          params.row.extendedEscalationDate ? new Date(params.row.extendedEscalationDate) : null,
        valueFormatter: (params) => formatDate(params.value),
      },
      {
        field: "status",
        headerName: "Status",
        flex: 1,
        type: "string",
        renderCell: (params) => (
          <Chip
            label={params.row.status?.replace(/_/g, " ")}
            color={getStatusLabelColor(params.row.status)}
            size="small"
          />
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        filterable: false,
        width: 170,
        minWidth: 170,
        maxWidth: 170,
        disableReorder: true,
      },
    ],
    []
  );

  const initialColumnVisibilityModel = {
    requesterEmail: false,
    requestOwnerEmail: false,
  };

  const renderActions = (params, rowNavigate, theme) => (
    <ThemeProvider theme={theme}>
      {isRegulatory && params.row.status === "PENDING" && (
        <Tooltip title="Actions">
          <MeatballsMenu options={meatballsMenuOptions(params.row)} />
        </Tooltip>
      )}

      <Tooltip title="Preview Request Reason">
        <IconButton
          color="info"
          onClick={() => openReasonPopup(params.row)}
          size="small"
        >
          <LightbulbOutlinedIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Preview Full Request">
        <IconButton
          color="primary"
          onClick={() => openDetailsPage(params.row)}
          size="small"
        >
          <ForwardToInboxIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Preview Exit Request">
        <IconButton
          color="error"
          onClick={() =>
            rowNavigate(
              DefaultRoutes.reqInfo.replace(":reqId", params.row.exitRequestId)
            )
          }
          size="small"
        >
          <ArrowCircleRightIcon />
        </IconButton>
      </Tooltip>
    </ThemeProvider>
  );

  const title = roleTitle[currentRole] || "Extension Requests";

  return (
    <>
      <GenericExitRequestListing
        title={title}
        PageIcon={<MoreTimeIcon color="white" fontSize="large" />}
        iconColor="primary.main"
        columns={columns}
        renderActions={renderActions}
        fetchPage={fetchPage}
        rows={rows}
        pageLoading={pageLoading}
        rowCount={rowCount}
        initialColumnVisibilityModel={initialColumnVisibilityModel}
      />

      <DelegationReasonPopup
        open={showReasonPopup}
        onClose={closeReasonPopup}
        title="Extension Request Reason"
        label="Extension Reason:"
        reason={selectedRequest?.reason}
        emptyMessage="No reason provided"
      />

      <DelegationActionPopup
        open={showActionPopup}
        onClose={closeActionPopup}
        onConfirm={handleRespond}
        actionType={actionType}
        loading={actionLoading}
        approveTitle="Approve Extension Request"
        declineTitle="Reject Extension Request"
        approvePrompt="Are you sure you want to approve this extension request?"
        declinePrompt="Please provide a response message for rejecting this extension request."
        declinePlaceholder="Please provide a reason for rejecting this extension request..."
        declineHelperText="Please provide a response message"
        approveButtonLabel="Yes, Approve"
        declineButtonLabel="Submit"
        cancelButtonLabel="Cancel"
      />
    </>
  );
};

export default ExtensionRequestsListing;
